import React, { FC, useEffect, useCallback, useState, ChangeEvent } from 'react'
import styled from 'styled-components'

// material-ui
import InputBase from '@material-ui/core/InputBase'
import { makeStyles } from '@material-ui/core/styles'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import Button from '@material-ui/core/Button'
import ArrowBackIos from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'

// local modules
import { LoadingIcon } from 'src/ui/molecules/dashboard-table-icons'
import { COLOR_WHITE, COLOR_LIGHT_GRAY } from 'src/constants'
export interface IPaginationProps {
  isLoading?: boolean
  count?: number
  pageSize?: number
  setPageSize?: (size: number) => void
  currentPage?: number
  setPage?: (page: number) => void
}

const StyledPaginationContainer = styled.div`
  width: 100%;
  max-height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
`
const StyledTotalPage = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-left: 8px;
  min-width: 24px;
`

const useStyles = makeStyles(theme => ({
  inputRoot: {
    color: 'inherit'
  },
  inputInput: {
    transition: theme.transitions.create('width'),
    width: '2em',
    height: '1.4em',
    margin: theme.spacing(0, 0, 0, 1.5),
    padding: theme.spacing(0, 0, 0, 0),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    textAlign: 'center',
    background: `${COLOR_WHITE}`,
    border: `1px solid ${COLOR_LIGHT_GRAY}`,
    borderRadius: '5px',
    lineHeight: 1
  },
  buttonPrev: {
    fontSize: '13px',
    lineHeight: 1
  },
  buttonNext: {
    fontSize: '13px',
    lineHeight: 1,
    margin: theme.spacing(0, 0, 0, 1.5)
  },
  select: {
    fontSize: '14px',
    fontWeight: 500,
    padding: theme.spacing(0, 0, 0, 0),
    margin: theme.spacing(0, 0, 0, 2.5),
    lineHeight: 1,
    '& .MuiSelect-root': {
      padding: '0px 24px 0px 0px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'end',
      lineHeight: '15px'
    },
    '&:hover': {
      textDecoration: 'underline'
    },
    '& > [aria-expanded="true"]': {
      textDecoration: 'underline'
    }
  }
}))

const menuStyles = makeStyles(theme => ({
  paper: {
    marginTop: '-30px',
    marginLeft: '-50px',
    backgroundColor: `${COLOR_WHITE}`
  }
}))

export const CustomPagination: FC<IPaginationProps> = ({
  count,
  isLoading,
  pageSize,
  setPageSize,
  currentPage,
  setPage
}) => {
  const classes = useStyles()
  const menuClasses = menuStyles()

  const [inputValue, setInputValue] = useState<string>('')
  const totalPage = Math.ceil(count / pageSize) ?? 1

  const handleSelectChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
      setPageSize(parseInt(value))
      setPage(1)
      setInputValue('1')
    },
    [setPage, setPageSize]
  )

  const handleInputValid = (value: string) => {
    if (parseInt(value) > totalPage) return totalPage
    if (parseInt(value) < 1) return 1
    return value
  }

  const handlePage = (calcValue: number) => {
    setPage(currentPage + calcValue)
  }

  const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/, '')
    const validValue = await handleInputValid(value)
    setInputValue(String(validValue))
  }

  useEffect(() => {
    setInputValue(String(currentPage))
  }, [currentPage, setInputValue])

  return (
    <StyledPaginationContainer>
      <Button
        className={classes.buttonPrev}
        disabled={currentPage === 1}
        onClick={() => handlePage(-1)}
        size='small'
        startIcon={<ArrowBackIos style={{ fontSize: 12 }} />}
      >
        Prev
      </Button>
      <InputBase
        classes={{
          root: classes.inputRoot,
          input: classes.inputInput
        }}
        disabled={isLoading}
        onPaste={(e)=>e.preventDefault()}
        onChange={handleInputChange}
        onKeyUp={({ keyCode }:{keyCode:number}) => (keyCode === 13) && inputValue !== 'NaN' || inputValue !== '' && setPage(parseInt(inputValue))}
        value={inputValue === 'NaN' ? '' : inputValue}
      />
      <StyledTotalPage>
        {isLoading ? <LoadingIcon size={24} /> : <span>/ {totalPage}</span>}
      </StyledTotalPage>
      <Button
        className={classes.buttonNext}
        disabled={totalPage === currentPage && !isLoading}
        endIcon={<ArrowForwardIosIcon style={{ fontSize: 12 }} />}
        onClick={() => handlePage(1)}
        size='small'
      >
        Next
      </Button>
      <Select
        className={classes.select}
        disableUnderline
        id='per_page'
        MenuProps={{ classes: menuClasses }}
        onChange={handleSelectChange}
        value={pageSize}
      >
        {[50, 100].map(pageSize => (
          <MenuItem
            key={pageSize}
            value={pageSize}
          >
            {pageSize} per page
          </MenuItem>
        ))}
      </Select>
    </StyledPaginationContainer>
  )
}
